/**
 * Permisos "protegidos" para eliminar archivos sensibles del detalle de archivo:
 * contabilización y archivos vinculados. Función pura que usa
 * documents/file.js.
 *
 * Regla (ver spec 2026-07-21):
 *  - Rol global root/contador (documents.root o el flag específico) → puede
 *    SIEMPRE, incluso en carpeta cerrada (corregir errores humanos).
 *  - Otro usuario (cliente / admin de empresa) → solo con el permiso general
 *    Y el específico asignados de forma EXPLÍCITA (efectivo por carpeta) y con
 *    la carpeta abierta.
 *  - El rol `admin` (level:'all') NO obtiene estos flags porque no están en
 *    FULL_PERMS/ADMIN_PERMS → cae al camino explícito y sin asignación queda en false.
 *
 * Guarda de contabilización (solo vinculados): un vínculo de un documento ya
 * contabilizado NO se puede eliminar por el camino explícito (cliente/admin de
 * empresa). root/contador SÍ pueden (corregir errores humanos), así que para
 * ellos la guarda no aplica. Por eso `hasAccounting` se pasa a canDeleteLinked
 * y solo afecta el camino explícito.
 *
 * Granular (2026-10-01): `deleteLinked` del efectivo ya trae la herencia (en
 * matrices viejas exige `delete`, ver permission_engine), así que aquí basta el flag.
 */
import { isSupervisor } from './closed_folder_perms';

const roleGrants = (globalRole, flag) => {
  const g = globalRole?.documents || {};
  return g.root === true || g[flag] === true;
};

// Con la carpeta cerrada solo borra la supervisora o root, sin importar los
// flags del rol (reunión 2026-09-18; ver lib/closed_folder_perms.js). Antes el
// rol contador borraba siempre, aun cerrada.
const blockedByClosing = (globalRole, isClosing) => !!isClosing && !isSupervisor(globalRole);

export const canDeleteLinked = ({ globalRole, effective, isClosing, hasAccounting = false }) => {
  if (blockedByClosing(globalRole, isClosing)) return false;
  if (roleGrants(globalRole, 'deleteLinked')) return true;
  const e = effective?.documents || {};
  return !!e.deleteLinked && !isClosing && !hasAccounting;
};

export const canDeleteAccounting = ({ globalRole, effective, isClosing }) => {
  if (blockedByClosing(globalRole, isClosing)) return false;
  if (roleGrants(globalRole, 'deleteAccounting')) return true;
  const e = effective?.documents || {};
  return !!(e.accounting && e.deleteAccounting) && !isClosing;
};

/**
 * Eliminar sin token (spec 2026-10-05): root concede por usuario
 * users/{uid}.SinToken = { document, accounting, linked, xml } (llega en
 * globalRole.sinToken, ver lib/user_scope.js). Con el flag, borra directo
 * con un confirmar: sin token y sin solicitud. Respeta la carpeta cerrada
 * (solo supervisora) y un documento contabilizado no se borra.
 */
export const canDeleteWithoutToken = ({ kind, globalRole, isClosing, hasAccounting = false, accounted = false }) => {
  if (!['document', 'accounting', 'linked', 'xml'].includes(kind)) return false;
  if (globalRole?.sinToken?.[kind] !== true) return false;
  if (blockedByClosing(globalRole, isClosing)) return false;
  if (kind === 'document' && (hasAccounting || accounted)) return false;
  return true;
};

// Flag que exige cada acción de borrado (actions/database/documents.js):
// files.delete → delete; deleteXML → deleteXml granular; deleteLink →
// deleteLinked; accountingThrowBack → accounting.
const NO_TOKEN_FLAGS = {
  document: { delete: true },
  xml: { deleteXml: true, granular: true },
  linked: { deleteLinked: true },
  accounting: { accounting: true },
};

/** Copia de `roles` (documents efectivos) con el flag que exige la acción. */
export const withNoTokenGrant = (kind, roles) => ({ ...(roles || {}), ...(NO_TOKEN_FLAGS[kind] || {}) });
